import { createClient } from '@supabase/supabase-js'

// Initialize the Supabase client (singleton pattern)
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables. Please check your .env file.')
}

// Create a true singleton instance of the Supabase client
// Using a module-level variable ensures only one instance exists
const supabaseInstance = createClient(
  supabaseUrl || '',
  supabaseAnonKey || '',
  {
    auth: {
      persistSession: true,
      storageKey: 'machinesearch-auth-storage'
    }
  }
);

// Export the singleton instance
export const supabase = supabaseInstance;

// Format Indian phone number for Supabase
const formatIndianPhoneNumber = (phone: string): string => {
  // Remove all non-digit characters
  let cleanedPhone = phone.replace(/\D/g, '');
  
  // If the number starts with 91 and is 12 digits long, remove the 91 prefix
  if (cleanedPhone.startsWith('91') && cleanedPhone.length === 12) {
    cleanedPhone = cleanedPhone.substring(2);
  }
  
  // Ensure the number is 10 digits long
  if (cleanedPhone.length !== 10) {
    throw new Error('Invalid phone number format. Must be 10 digits.');
  }
  
  // Return the number with the required + prefix for Supabase
  return `+91${cleanedPhone}`;
};

// Phone authentication helpers
export const sendOTP = async (phone: string) => {
  try {
    // Format the phone number for Supabase
    const formattedPhone = formatIndianPhoneNumber(phone);
    
    const { data, error } = await supabase.auth.signInWithOtp({
      phone: formattedPhone,
    })
    
    if (error) throw error
    return { success: true, data }
  } catch (error: any) {
    console.error('Error sending OTP:', error.message)
    
    // Handle specific error cases
    if (error.message?.includes('Invalid template')) {
      return { 
        success: false, 
        error: 'There is an issue with our SMS provider. Please try again later or contact support.'
      }
    }
    
    // For development purposes, you might want to see the actual error
    console.log('Detailed OTP error:', error)
    
    return { success: false, error: error.message }
  }
}

export const verifyOTP = async (phone: string, token: string) => {
  try {
    // Format the phone number for Supabase
    const formattedPhone = formatIndianPhoneNumber(phone);
    
    const { data, error } = await supabase.auth.verifyOtp({
      phone: formattedPhone,
      token,
      type: 'sms'
    })
    
    if (error) throw error
    return { success: true, data }
  } catch (error: any) {
    console.error('Error verifying OTP:', error.message)
    return { success: false, error: error.message }
  }
}

// Get current user
export const getCurrentUser = async () => {
  try {
    const { data: { user }, error } = await supabase.auth.getUser()
    if (error) {
      // Don't log AuthSessionMissingError as it's expected when not logged in
      if (error.message !== 'Auth session missing!') {
        console.error('Error getting user:', error)
      }
      return null
    }
    return user
  } catch (error) {
    // Only log unexpected errors
    if (error instanceof Error && error.message !== 'Auth session missing!') {
      console.error('Error getting user:', error)
    }
    return null
  }
}

// Sign out
export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
    return { success: true }
  } catch (error: any) {
    console.error('Error signing out:', error.message)
    return { success: false, error: error.message }
  }
}

/**
 * Creates a user profile if it doesn't exist
 * @param userId The user's ID
 * @param phone The user's phone number
 * @returns Object indicating success or failure
 */
/**
 * Adds an RLS policy to allow users to insert their own accounts
 */
export const addProfileInsertPolicy = async () => {
  try {
    // Execute raw SQL to create the policy
    // We'll use the supabase.auth.admin API which requires service_role key
    const { error: sqlError } = await supabase.rpc('execute_system_command', {
      command: 'sql',
      args: {
        query: `
          -- Drop the policy if it exists to avoid errors
          DROP POLICY IF EXISTS "Users can insert their own account" ON accounts;
          
          -- Create the new policy
          CREATE POLICY "Users can insert their own account"
            ON accounts
            FOR INSERT
            WITH CHECK (auth.uid() = id);
        `
      }
    });
    
    if (sqlError) {
      console.error('Error creating policy:', sqlError);
      return { success: false, error: sqlError.message };
    }
    
    return { success: true, message: 'Policy created successfully' };
  } catch (error: any) {
    console.error('Error creating policy:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Creates a user profile if it doesn't exist
 * @param userId The user's ID
 * @param phone The user's phone number
 * @returns Object indicating success or failure
 */
export const createUserProfileIfNotExists = async (userId: string, phone: string) => {
  console.log(`Attempting to create/verify profile for user ${userId} with phone ${phone}`);
  
  try {
    // First check if a profile already exists for this user
    console.log('Checking for existing profile...');
    const { data: existingProfile, error: fetchError } = await supabase
      .from('accounts')
      .select('*')
      .eq('id', userId)
      .single();
    
    // If profile already exists, return success
    if (existingProfile) {
      console.log('Existing profile found:', existingProfile);
      return { success: true, isNew: false, profile: existingProfile };
    }
    
    // If there was an error other than 'not found', throw it
    if (fetchError) {
      if (fetchError.code === 'PGRST116') {
        console.log('No existing profile found, will create new one');
      } else {
        console.error('Error fetching profile:', fetchError);
        throw fetchError;
      }
    }
    
    // Profile doesn't exist, create it
    const formattedPhone = formatIndianPhoneNumber(phone);
    console.log(`Creating new profile with formatted phone: ${formattedPhone}`);
    
    // Create the minimal required profile
    const profileData = {
      id: userId,
      phone: formattedPhone,
      user_type: 'buyer', // Default user type
      is_verified: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    console.log('Inserting profile with data:', profileData);
    
    // Use direct insert - this will work once the RLS policy is in place
    const { data: newProfile, error: insertError } = await supabase
      .from('accounts')
      .insert(profileData)
      .select()
      .single();
    
    if (insertError) {
      console.error('Error inserting profile:', insertError);
      throw insertError;
    }
    
    console.log('Profile created successfully:', newProfile);
    return { success: true, isNew: true, profile: newProfile };
  } catch (error: any) {
    console.error('Error creating user profile:', error);
    // More detailed error information
    const errorMessage = error.message || 'Unknown error';
    const errorCode = error.code || 'No error code';
    const errorDetails = error.details || 'No details';
    
    console.error(`Error details - Message: ${errorMessage}, Code: ${errorCode}, Details: ${errorDetails}`);
    
    return { 
      success: false, 
      error: errorMessage,
      errorCode,
      errorDetails
    };
  }
}
